/**
 * MatchConfig — singleton entity carrying gameplay tuning.
 *
 * Boot flow (PSV-1898 — subscription-first):
 *   `module_dataflow.DataGetRequest` no longer exists on the platform, so
 *   the old "one-shot DataGet seed, then listen for deltas" flow is dead.
 *   Instead we open a collection-form DataSubscription and take its INITIAL
 *   Overwrite push as the seed (verified live on the authenticated
 *   handshake — the new platform DOES push current state on subscribe).
 *   Until that push lands we run on compiled FALLBACKS so BootScene never
 *   blocks; the push simply applies whenever it arrives.
 *
 * Why a RAW subscription rather than the SDK `.listen()`: on the new
 * platform MatchConfig is a SINGLETON whose row id is a UUID, so the keyed
 * form (`MatchConfig(id: "matchconfig")`) matches nothing, and the SDK
 * requires `.key()` for `listen()` (collection listen is PSV-1347 backlog).
 * The id-less collection query reads/watches the whole (one-row) table —
 * same approach as the server's `subscribeMatchConfig`.
 *
 * Update handling mirrors the server:
 *   - Overwrite → unwrap `[row]`/bare row, normalize, diff-log, swap cache.
 *   - Patch     → partial flat row; merge into current locally (fields are
 *     all flat numbers), treat as Overwrite. No refetch — DataGet is gone.
 *
 * FALLBACKS are kept as a safety net — a network blip or transient DataFlow
 * outage shouldn't black-hole the demo; we log loudly and run on compiled
 * defaults instead. Keep them in lockstep with server MATCHCONFIG_DEFAULTS.
 */

import { Commands, type PlayServClient } from 'playserv-sdk';

export interface MatchConfig {
  ArenaRadius: number;
  MaxPlayers: number;
  ShipSpeed: number;
  ShipAcceleration: number;
  ShipRotationSpeed: number;
  ShipDrag: number;
  ShipMaxHP: number;
  ProjectileSpeed: number;
  ProjectileDamage: number;
  ProjectileLifetime: number;
  ReloadTime: number;
  RespawnDelay: number;
  GhostDuration: number;
  BroadcastRateHz: number;
  /** PSV-1887 — hot zone + world events (live-tunable, Stage 7). */
  HotZoneRadius: number;
  HotZoneGemMult: number;
  GoldRushPeriodS: number;
  GoldRushDurationS: number;
  StormPeriodS: number;
  StormDurationS: number;
}

const FALLBACKS: MatchConfig = {
  ArenaRadius: 800,
  MaxPlayers: 32,
  ShipSpeed: 320,
  ShipAcceleration: 220,
  // Keep in lockstep with server MATCHCONFIG_DEFAULTS + the seeded
  // platform singleton (PSV-1785 turn-radius tuning: 3 → 4.5 rad/s).
  ShipRotationSpeed: 4.5,
  ShipDrag: 0.98,
  ShipMaxHP: 100,
  ProjectileSpeed: 600,
  ProjectileDamage: 10,
  ProjectileLifetime: 1.5,
  ReloadTime: 0.5,
  RespawnDelay: 3,
  GhostDuration: 2,
  BroadcastRateHz: 20,
  // PSV-1887 — keep in lockstep with server MATCHCONFIG_DEFAULTS.
  HotZoneRadius: 180,
  HotZoneGemMult: 2.5,
  GoldRushPeriodS: 180,
  GoldRushDurationS: 45,
  StormPeriodS: 420,
  StormDurationS: 25,
};

const FIELD_NAMES = Object.keys(FALLBACKS) as Array<keyof MatchConfig>;
const COLLECTION_QUERY = `MatchConfig { ${FIELD_NAMES.join(' ')} }`;
// Bounded window for the initial push; on timeout we run on FALLBACKS and
// the push applies whenever it lands.
const SEED_TIMEOUT_MS = 4_000;

interface SubscriptionUpdateFrame {
  RequestId?: string | number;
  DataSubscriptionId?: number;
  UpdateType?: string;
  Data?: unknown;
}

/** Collection form delivers `[row]`; tolerate a bare object. */
function unwrapSingleton(raw: unknown): Record<string, unknown> | null {
  const body = Array.isArray(raw) ? (raw[0] as unknown) : raw;
  if (body === null || body === undefined || typeof body !== 'object') {
    return null;
  }
  return body as Record<string, unknown>;
}

let _matchConfig: MatchConfig | null = null;
let _offUpdates: (() => void) | null = null;

export async function loadMatchConfig(client: PlayServClient): Promise<MatchConfig> {
  // Idempotent: BootScene and ArenaController both call this — one live
  // subscription is enough (a second one doubles frames and logs).
  if (_offUpdates !== null && _matchConfig !== null) return _matchConfig;
  _matchConfig = { ...FALLBACKS };

  // PSV-2016 #5 — public raw-request API replaces the @internal
  // `_connection()`. raw() itself never throws; its methods throw
  // NotReadyError while offline — the existing try/catch below already
  // routes that to the FALLBACKS path.
  const conn = client.raw();

  let seeded = false;
  let resolveSeed: () => void = () => {};
  const seedArrived = new Promise<void>((resolve) => {
    resolveSeed = resolve;
  });

  const applyFrame = (frame: SubscriptionUpdateFrame, reason: string): void => {
    // Frames wrap the row(s) under the entity name or deliver them bare.
    const body =
      typeof frame.Data === 'object' &&
      frame.Data !== null &&
      'MatchConfig' in (frame.Data as object)
        ? (frame.Data as { MatchConfig?: unknown }).MatchConfig
        : frame.Data;
    const raw = unwrapSingleton(body);
    if (raw === null) {
      // eslint-disable-next-line no-console
      console.warn(`[matchconfig] ${reason}: malformed payload — ignored`);
      return;
    }
    const prev = _matchConfig ?? { ...FALLBACKS };
    // Patch = partial flat row → merge into current before normalizing;
    // Overwrite carries the full row so the merge is a no-op for it.
    const merged =
      frame.UpdateType === 'Patch' ? { ...prev, ...raw } : raw;
    const next = normalize(merged as Record<string, unknown>);
    const diff = diffMatchConfig(prev, next);
    if (diff.length > 0) {
      // eslint-disable-next-line no-console
      console.info(`[matchconfig] config updated: { ${diff.join(', ')} }`);
      _matchConfig = next;
    }
    if (!seeded) {
      seeded = true;
      // eslint-disable-next-line no-console
      console.info(
        `[matchconfig] initial config via subscription push: ShipSpeed=${next.ShipSpeed}, ArenaRadius=${next.ArenaRadius}, BroadcastRateHz=${next.BroadcastRateHz}`,
      );
      resolveSeed();
    }
  };

  try {
    const { requestId, promise } = conn.requestWithId(
      Commands.DataSubscriptionRequest,
      { Query: COLLECTION_QUERY, Variables: {} },
    );

    // Register the frame listener BEFORE awaiting the response — the
    // initial Overwrite can race the DataSubscriptionResponse.
    let subscriptionId: number | null = null;
    const buffered: SubscriptionUpdateFrame[] = [];
    _offUpdates = conn.onSystemEvent('DataSubscriptionUpdate', (payload) => {
      const frame = payload as SubscriptionUpdateFrame;
      if (subscriptionId === null) {
        if (frame.RequestId === requestId) buffered.push(frame);
        return;
      }
      if (
        frame.DataSubscriptionId === subscriptionId ||
        frame.RequestId === requestId
      ) {
        applyFrame(frame, `push(${String(frame.UpdateType)})`);
      }
    });

    const resp = (await promise) as {
      Result?: { SubscriptionId?: number };
    };
    const sid = resp.Result?.SubscriptionId;
    if (typeof sid !== 'number') {
      throw new Error('DataSubscriptionResponse missing SubscriptionId');
    }
    subscriptionId = sid;
    for (const frame of buffered) {
      applyFrame(frame, `buffered(${String(frame.UpdateType)})`);
    }
    buffered.length = 0;
    // eslint-disable-next-line no-console
    console.info(
      `[matchconfig] subscribed (subscriptionId=${sid}), awaiting initial push...`,
    );
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(
      '[matchconfig] subscribe failed — running on FALLBACKS (no live updates):',
      err,
    );
    stopUpdates();
    return _matchConfig;
  }

  await Promise.race([
    seedArrived,
    new Promise<void>((resolve) => {
      setTimeout(resolve, SEED_TIMEOUT_MS);
    }),
  ]);
  if (!seeded) {
    // eslint-disable-next-line no-console
    console.warn(
      `[matchconfig] no initial push within ${SEED_TIMEOUT_MS}ms — running on FALLBACKS; live updates will still apply`,
    );
  }
  return _matchConfig;
}

export function getMatchConfig(): MatchConfig {
  if (_matchConfig === null) {
    throw new Error('MatchConfig not loaded. Call loadMatchConfig() first.');
  }
  return _matchConfig;
}

function stopUpdates(): void {
  if (_offUpdates !== null) {
    const off = _offUpdates;
    _offUpdates = null;
    try {
      off();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[matchconfig] update listener teardown failed:', err);
    }
  }
}

export async function stopMatchConfig(): Promise<void> {
  // No server-side unsubscribe on the client (matches the old ListenHandle
  // semantics — the subscription dies with the WS session); just stop
  // routing frames into the local cache.
  stopUpdates();
  return Promise.resolve();
}

function normalize(raw: Record<string, unknown>): MatchConfig {
  const result = {} as MatchConfig;
  for (const field of FIELD_NAMES) {
    const value = raw[field];
    if (typeof value === 'number' && Number.isFinite(value)) {
      result[field] = value;
    } else {
      result[field] = FALLBACKS[field];
    }
  }
  return result;
}

function diffMatchConfig(prev: MatchConfig, next: MatchConfig): string[] {
  const diff: string[] = [];
  for (const field of FIELD_NAMES) {
    if (prev[field] !== next[field]) {
      diff.push(`${field}: ${prev[field]} → ${next[field]}`);
    }
  }
  return diff;
}
